import { Injectable, OnDestroy, inject } from '@angular/core';
import {
	BehaviorSubject,
	Observable,
	Subscription,
	filter,
	lastValueFrom,
	map
} from 'rxjs';
import { QuestionnaireService } from 'src/app/api/tomo-questionnaires-api/services';
import {
	TomoQuestionnaire,
	TomoQuestionnairePagedResult
} from 'src/app/api/tomo-questionnaires-api/models';

@Injectable({
	providedIn: 'root'
})
export class QuestionnaireHelperService implements OnDestroy {
	private questionnaireService = inject(QuestionnaireService);

	private questionnaires: BehaviorSubject<Array<TomoQuestionnaire>> =
		new BehaviorSubject<Array<TomoQuestionnaire>>(null);

	public questionnaires$: Observable<Array<TomoQuestionnaire>> =
		this.questionnaires.pipe(
			filter(
				(questionnaires) =>
					questionnaires != null && questionnaires != undefined
			)
		);

	private apiSub: Subscription;

	public initialize(): void {
		this.apiSub = this.questionnaireService
			.getTomoquestionnaire$Json()
			.subscribe({
				next: (response: TomoQuestionnairePagedResult) => {
					const questionnaireJson = response.results;
					this.questionnaires.next(questionnaireJson);
				},
				error: (error: unknown) => {
					throw error;
				}
			});
	}

	ngOnDestroy(): void {
		if (this.apiSub) {
			this.apiSub.unsubscribe();
		}
	}

	public getQuestionnaireById(
		id: string
	): Observable<TomoQuestionnaire | undefined> {
		return this.questionnaires$.pipe(
			map((questionnaires) => questionnaires.find((q) => q.id === id))
		);
	}

	public async createQuestionnaire(
		data: TomoQuestionnaire
	): Promise<TomoQuestionnaire> {
		try {
			const response: TomoQuestionnaire = await lastValueFrom(
				this.questionnaireService.postTomoquestionnaire$Json({
					body: data
				})
			);
			this.refreshQuestionnaires();
			return response;
		} catch (error: unknown) {
			console.error('Error creating questionnaire:', error);
			throw error; // Rethrow the error for further handling if needed
		}
	}

	public updateQuestionnaire(
		questionnaire: Partial<TomoQuestionnaire>
	): Observable<void> {
		return new Observable<void>((observer) => {
			this.questionnaireService
				.putTomoquestionnaireId$Json({
					id: questionnaire.id,
					body: questionnaire
				})
				.subscribe({
					next: () => {
						this.refreshQuestionnaires();
						observer.next();
						observer.complete();
					},
					error: (error) => {
						console.error('Error updating questionnaire:', error);
						observer.error(error);
					}
				});
		});
	}

	public deleteQuestionnaire(id: string): Observable<void> {
		return new Observable<void>((observer) => {
			this.questionnaireService
				.deleteTomoquestionnaireId$Response({
					id: id
				})
				.subscribe({
					next: () => {
						this.refreshQuestionnaires();
						observer.next();
						observer.complete();
					},
					error: (error) => {
						console.error('Error deleting questionnaire:', error);
						observer.error(error);
					}
				});
		});
	}

	public refreshQuestionnaires(): void {
		this.questionnaireService.getTomoquestionnaire$Json().subscribe({
			next: (response: TomoQuestionnairePagedResult) => {
				const questionnaireJson = response.results;
				this.questionnaires.next(questionnaireJson);
			},
			error: (error: unknown) => {
				console.error('Error refreshing questionnaires:', error);
			}
		});
	}
}
